import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, TrendingUp, Package, RefreshCw, ShoppingCart } from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

export default function CommonalityAnalysis() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { filters, setFilters } = useGlobalFilters();

  const buildQueryString = useCallback((filterObj) => {
    const params = new URLSearchParams();
    if (filterObj.category) params.append('category_l1', filterObj.category);
    if (filterObj.category_l2) params.append('category_l2', filterObj.category_l2);
    if (filterObj.category_l3) params.append('category_l3', filterObj.category_l3);
    if (filterObj.supplier) params.append('supplier', filterObj.supplier);
    if (filterObj.region) params.append('region', filterObj.region);
    if (filterObj.country) params.append('country', filterObj.country);
    if (filterObj.sbu) params.append('sbu', filterObj.sbu);
    if (filterObj.cost_center) params.append('cost_center', filterObj.cost_center);
    if (filterObj.business_unit) params.append('business_unit', filterObj.business_unit);
    if (filterObj.addressability) params.append('addressability', filterObj.addressability);
    if (filterObj.fiscal_year) params.append('fiscal_year', filterObj.fiscal_year);
    if (filterObj.startDate) params.append('start_date', filterObj.startDate.split('T')[0]);
    if (filterObj.endDate) params.append('end_date', filterObj.endDate.split('T')[0]);
    return params.toString();
  }, []);

  const fetchData = useCallback(async (filterObj = filters) => {
    setLoading(true);
    try {
      const qs = buildQueryString(filterObj);
      const response = await api.get(`/analytics/commonality${qs ? `?${qs}` : ''}`);
      setData(response.data);
    } catch {
      toast.error('Failed to load commonality analysis');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryString, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const handleRefresh = () => { setRefreshing(true); fetchData(filters); };

  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== '' && v !== 'all').length;

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['--- Summary ---'],
      ['Common Suppliers', data.common_suppliers?.length || 0],
      ['Total Locations', data.total_departments || 0],
      ['Consolidation Opps', data.common_suppliers?.filter(s => s.consolidation_opportunity).length || 0],
      ['Items Shared by Multiple Suppliers', data.item_commonality?.length || 0],
      [],
      ['--- Common Suppliers ---'],
      ['Supplier', 'Locations', 'Total Spend', 'Categories', 'High Priority'],
      ...(data.common_suppliers || []).map(s => [s.supplier, s.department_count, s.total_spend, (s.categories || []).join('; '), s.consolidation_opportunity ? 'Yes' : 'No']),
      [],
      ['--- Item Commonality ---'],
      ['Item', 'Level', 'Supplier Count', 'Total Spend', 'Transactions', 'Suppliers'],
      ...(data.item_commonality || []).map(i => [i.item, i.level, i.supplier_count, i.total_spend, i.transaction_count, (i.suppliers || []).join('; ')]),
      [],
      ['--- Standardization Opportunities ---'],
      ['Category', 'Supplier Count', 'Opportunity'],
      ...(data.standardization_opportunities || []).map(o => [o.category, o.supplier_count, o.opportunity]),
    ];
    downloadCSV(rows, 'commonality_analysis');
  };

  const exportPDF = () => {
    if (!data) return;
    buildPDF([
      { title: 'Summary', headers: ['Metric', 'Value'], rows: [['Common Suppliers', String(data.common_suppliers?.length || 0)], ['Total Locations', String(data.total_departments || 0)], ['Items (Multi-Supplier)', String(data.item_commonality?.length || 0)]] },
      { title: 'Common Suppliers', headers: ['Supplier', 'Locations', 'Spend', 'Priority'], rows: (data.common_suppliers || []).map(s => [s.supplier, String(s.department_count), `${cs}${s.total_spend?.toLocaleString()}`, s.consolidation_opportunity ? 'Yes' : 'No']) },
      { title: 'Item Commonality', headers: ['Item', 'Level', 'Suppliers', 'Spend'], rows: (data.item_commonality || []).map(i => [i.item, i.level, String(i.supplier_count), `${cs}${i.total_spend?.toLocaleString()}`]) },
    ], 'Commonality_Analysis');
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-4" data-testid="commonality-analysis-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Commonality Analysis</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Multi-location supplier analysis and consolidation opportunities
            {activeFiltersCount > 0 && <span className="ml-2 text-primary font-medium">({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Standardized Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-common-suppliers">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Common Suppliers <HelpBubble text="Suppliers used by 2+ locations or departments. Consolidating their contract terms across the group is usually the quickest win here." /></CardTitle>
            <Users className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.common_suppliers?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Used by multiple locations</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-locations">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Total Locations <HelpBubble text="Distinct locations, plants, or sites with spend. Used as the denominator when calculating supplier / item commonality percentages." /></CardTitle>
            <Package className="w-5 h-5 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.total_departments || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Active purchasing units</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-consolidation">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Consolidation Opps <HelpBubble text="Categories where different locations use different suppliers for essentially the same goods/services — prime candidates for a single global agreement." /></CardTitle>
            <TrendingUp className="w-5 h-5 text-chart-4" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.common_suppliers?.filter(s => s.consolidation_opportunity).length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">High-priority targets</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-item-commonality">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Common Items <HelpBubble text="Items or materials purchased across multiple locations. Standardising specifications reduces SKU sprawl and unlocks volume discounts." /></CardTitle>
            <ShoppingCart className="w-5 h-5 text-chart-5" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.item_commonality?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Items from multiple suppliers</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Supplier vs Item Commonality */}
      <Tabs defaultValue="suppliers" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Supplier Commonality</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Item Commonality</TabsTrigger>
          <TabsTrigger value="standardization" data-testid="tab-standardization">Standardization</TabsTrigger>
        </TabsList>

        {/* Supplier Commonality Tab */}
        <TabsContent value="suppliers">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="common-suppliers-section">
              <CardHeader>
                <CardTitle>Common Suppliers Across Locations</CardTitle>
                <CardDescription>Suppliers used by multiple locations - opportunities for centralized negotiations</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.common_suppliers?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No common suppliers found. Upload data with Office Location field to see analysis.</p>
                ) : (
                  <div className="space-y-3">
                    {data?.common_suppliers?.slice(0, 20).map((supplier, index) => (
                      <motion.div
                        key={supplier.supplier}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="p-4 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{supplier.supplier}</h3>
                              {supplier.consolidation_opportunity && <Badge variant="default">High Priority</Badge>}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                              <div><span className="font-medium">Locations:</span> {supplier.department_count}</div>
                              <div><span className="font-medium">Total Spend:</span> {cs}{supplier.total_spend?.toLocaleString()}</div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <span className="text-xs font-medium text-muted-foreground mr-1">Categories:</span>
                              {supplier.categories?.map((category, catIndex) => (
                                <Badge key={catIndex} variant="secondary" className="text-xs font-normal">{category}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Item Commonality Tab */}
        <TabsContent value="items">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="item-commonality-section">
              <CardHeader>
                <CardTitle>Item Commonality Across Suppliers</CardTitle>
                <CardDescription>Items and subcategories provided by multiple suppliers - compare pricing and consolidate</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.item_commonality?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No common items found across suppliers.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Item</th>
                          <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Level</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Suppliers</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Total Spend</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Transactions</th>
                          <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Supplier Names</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.item_commonality || []).map((item, idx) => (
                          <tr key={`${item.level}-${item.item}`} className="border-b border-border hover:bg-muted/50 transition-colors">
                            <td className="py-1.5 px-3 font-medium text-sm">{item.item}</td>
                            <td className="py-1.5 px-3 text-center">
                              <Badge variant={item.level === 'L2' ? 'default' : 'secondary'} className="text-xs">{item.level}</Badge>
                            </td>
                            <td className="py-1.5 px-3 text-right font-mono text-sm font-semibold text-primary">{item.supplier_count}</td>
                            <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{item.total_spend?.toLocaleString()}</td>
                            <td className="py-1.5 px-3 text-right font-mono text-sm">{item.transaction_count?.toLocaleString()}</td>
                            <td className="py-1.5 px-3 text-sm">
                              <div className="flex flex-wrap gap-1 max-w-md">
                                {item.suppliers?.slice(0, 5).map((s, i) => (
                                  <Badge key={i} variant="outline" className="text-xs font-normal">{s.length > 25 ? s.slice(0, 23) + '...' : s}</Badge>
                                ))}
                                {item.suppliers?.length > 5 && (
                                  <Badge variant="outline" className="text-xs">+{item.suppliers.length - 5} more</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Standardization Tab */}
        <TabsContent value="standardization">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="standardization-opportunities">
              <CardHeader>
                <CardTitle>Standardization Opportunities</CardTitle>
                <CardDescription>Categories with multiple suppliers - consider consolidation for better pricing</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.standardization_opportunities?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No standardization opportunities identified</p>
                ) : (
                  <div className="space-y-3">
                    {data?.standardization_opportunities?.map((opp, index) => (
                      <div key={index} className="p-4 rounded-lg border border-border hover:border-secondary/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{opp.category}</p>
                            <p className="text-sm text-muted-foreground mt-1">{opp.opportunity}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold font-mono text-secondary">{opp.supplier_count}</p>
                            <p className="text-xs text-muted-foreground">suppliers</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Insights */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardHeader><CardTitle>Key Insights</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                <p className="text-sm">
                  <strong>{data?.common_suppliers?.filter(s => s.department_count >= 3).length || 0}</strong> suppliers
                  are used by 3 or more locations - prime candidates for enterprise agreements
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-secondary mt-2" />
                <p className="text-sm">
                  <strong>{data?.item_commonality?.length || 0}</strong> items are sourced from multiple suppliers - 
                  compare unit pricing to identify the best-value providers
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-chart-4 mt-2" />
                <p className="text-sm">
                  Consolidating suppliers in <strong>{data?.standardization_opportunities?.length || 0}</strong> categories
                  could reduce administrative overhead and improve pricing leverage
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      <AIPanel page="commonality" filters={filters} />
    </div>
  );
}